/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/personal-info.tsx (the `Form`
 * + `FormFooter` shell every FORM screen in this migration uses) and
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/create-category.tsx (edit
 * mode — the page-with-back-button + FormGroup field layout).
 *
 * Only the data, columns and labels are ours. Reuses the same sources brand's own Detail.tsx cites
 * (screen-map.md row 12).
 */
import { useState } from "react";
import { useLoaderData, useNavigate, useOutletContext, type LoaderFunctionArgs } from "react-router-dom";
import { z } from "zod";
import toast from "react-hot-toast";
import {
  PiIdentificationBadgeDuotone as DetailsIcon,
  PiImageDuotone as ImageIcon,
  PiTagDuotone as ClassificationIcon,
  PiToggleRightDuotone as StatusIcon,
  PiXBold as RemoveIcon,
} from "react-icons/pi";
import FormFooter from "@core/components/form-footer";
import Upload from "@core/ui/upload";
import { PageHeader, ErrorSurface, Field, FormGroup, Form } from "@steelyard/theme";
import { SelectField } from "../../../components/select-field";
import { StatusSwitch } from "../../../components/status-switch";
import {
  deactivateProduct,
  byKey,
  getProduct,
  getProductBySlug,
  activeOrKept,
  listBrands,
  listCategoryTree,
  reactivateProduct,
  updateProduct,
  type Brand,
  type Category,
} from "../../../lib/services/catalog";
import { imageUrl, useFileUpload, type UploadedFile } from "@steelyard/common";
import { can } from "../../../lib/permissions";
import { formCard, formCardFooter, formSections } from "../../../lib/form-layout";
import { SectionTitle } from "../../../components/section-title";
import type { AccountSummary, PermissionState } from "../../../types";
import { ActionIcon, Text, Textarea } from "rizzui";
import { UploadDropHint, UploadBusyHint } from "../../../components/upload-drop-hint";
import { useGoBack } from "../../../lib/navigation";
import { changedSlug, SLUG_HINT, slugSchema } from "../../../lib/slug";
import { productPath } from "../../../lib/paths";

/** Both calls this screen needs: the product itself, and the brand/category pickers — active only,
 * plus the product's own brand/category if since deactivated (`activeOrKept`). */
export async function productDetailLoader({ params, request }: LoaderFunctionArgs) {
  const [product, brands, categories] = await Promise.all([
    byKey(request, params.slug!, getProduct, getProductBySlug, productPath),
    listBrands(1, false, 200),
    listCategoryTree(),
  ]);
  if (!product || !brands || !categories) throw new Error("The API returned no body for this product.");
  return {
    product,
    brands: activeOrKept(brands, [product.brandId]),
    categories: activeOrKept(categories, [product.categoryId]),
  };
}

const productEditSchema = z.object({
  brandId: z.string().trim().min(1, "Required"),
  categoryId: z.string().trim().min(1, "Required"),
  name: z.string().trim().min(1, "Required").max(200),
  slug: slugSchema,
  description: z.string().trim().max(2000).optional().or(z.literal("")),
  isActive: z.boolean(),
});
type ProductEditValues = z.infer<typeof productEditSchema>;

export default function ProductDetail() {
  const { product, brands, categories } = useLoaderData() as Awaited<ReturnType<typeof productDetailLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  // Same as brands/Detail.tsx: Back is a history step, and the bare list path is the Active view.
  const goBack = useGoBack("/catalogue/products");
  const navigate = useNavigate();
  const [error, setError] = useState<unknown>(null);
  // `imageHash` is what actually renders — seeded from the product's own persisted
  // `primaryImageHash`, so a reload shows the existing image rather than an empty upload box.
  // `uploadedImage` only ever holds a fresh upload's size/content-type, for the caption under it —
  // `ProductView` doesn't carry those, so a persisted-only image shows the picture with no caption.
  const [imageHash, setImageHash] = useState<string | null>(product.primaryImageHash);
  const [uploadedImage, setUploadedImage] = useState<UploadedFile | null>(null);
  // Upload to Files, then point the product at the hash. The persisted fields go back unchanged.
  const packingImage = useFileUpload("image", {
    onUploaded: async (result) => {
      await updateProduct(product.id, {
        brandId: product.brandId,
        categoryId: product.categoryId,
        name: product.name,
        description: product.description,
        images: product.images.length > 0 ? product.images : null,
        primaryImageHash: result.hash,
      });
      setUploadedImage(result);
      setImageHash(result.hash);
      toast.success("Image uploaded");
    },
    onError: () => toast.error("Could not upload the image"),
  });
  // Gallery images (Product.images) — content-addressed hashes, seeded from the persisted product.
  const [images, setImages] = useState<string[]>(product.images);
  const gallery = useFileUpload("image", {
    multiple: true,
    onUploaded: (uploaded, files) => {
      setImages((prev) => [...prev, ...uploaded.map((result) => result.hash)]);
      toast.success(files.length > 1 ? "Images uploaded" : "Image uploaded");
    },
    onError: () => toast.error("Could not upload the image(s)"),
  });

  if (!can(permissionState, "catalogue.read")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="view this product" />;
  }
  const canWrite = can(permissionState, "catalogue.administer");

  function removeImage(hash: string) {
    setImages((prev) => prev.filter((h) => h !== hash));
  }

  return (
    <div className="flex flex-col gap-6 @container">
      <PageHeader
        title={product.name}
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Catalogue" }, { name: "Products", href: "/catalogue/products" }, { name: product.name }]}
      />

      {error ? <ErrorSurface error={error} action="save this product" /> : null}

      <Form<ProductEditValues>
        validationSchema={productEditSchema}
        useFormProps={{
          defaultValues: {
            brandId: product.brandId,
            categoryId: product.categoryId,
            name: product.name,
            slug: product.slug,
            description: product.description ?? "",
            isActive: product.isActive,
          },
        }}
        className={formCard}
        onSubmit={async (values) => {
          setError(null);
          // Set once the update has saved a new slug: from then on the old slug's URL reads nothing.
          let savedSlug: string | undefined;
          // A slug change makes the new address the record's only one (there is no slug history), so
          // the page moves there, replacing the old entry rather than leaving it in history.
          const moveTo = (slug: string) => navigate(productPath(slug), { replace: true });
          try {
            const slug = changedSlug(values.slug, product.slug);
            await updateProduct(product.id, {
              brandId: values.brandId,
              categoryId: values.categoryId,
              name: values.name,
              description: values.description || null,
              images: images.length > 0 ? images : null,
              slug,
            });
            savedSlug = slug;
            // The status is its own call (deactivate never deletes — FR-007), made only when the box changed.
            if (values.isActive !== product.isActive) await (values.isActive ? reactivateProduct : deactivateProduct)(product.id);
            toast.success("Product saved");
            if (slug) moveTo(slug);
            else goBack();
          } catch (err) {
            setError(err);
            toast.error("Could not save the product");
            // The update went through and only the status call failed: show that on the new address.
            if (savedSlug) moveTo(savedSlug);
          }
        }}
      >
        {({ register, formState: { errors, isSubmitting } }) => (
          <>
            <div className={formSections}>
              <FormGroup title={<SectionTitle icon={ClassificationIcon}>Classification</SectionTitle>} description="Brand and category decide which discount rules can match this product's SKUs.">
                <SelectField label="Brand" required disabled={!canWrite} error={errors.brandId?.message} {...register("brandId")}>
                  {brands.items.map((b: Brand) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </SelectField>
                <SelectField label="Category" required disabled={!canWrite} error={errors.categoryId?.message} {...register("categoryId")}>
                  {categories.items.map((c: Category) => (
                    <option key={c.id} value={c.id}>
                      {"  ".repeat(c.depth)}
                      {c.name}
                    </option>
                  ))}
                </SelectField>
              </FormGroup>
              <FormGroup title={<SectionTitle icon={DetailsIcon}>Details</SectionTitle>} description="What customers see on the product page.">
                <Field label="Name" required maxLength={200} disabled={!canWrite} wrapperClassName="col-span-full" error={errors.name?.message} {...register("name")} />
                <Field
                  label="URL slug"
                  maxLength={200}
                  required
                  className="font-mono"
                  disabled={!canWrite}
                  hint={SLUG_HINT}
                  error={errors.slug?.message}
                  {...register("slug")}
                />
                <Textarea
                  label="Description"
                  rows={3}
                  className="col-span-full"
                  disabled={!canWrite}
                  helperText="Optional."
                  error={errors.description?.message}
                  {...register("description")}
                />
              </FormGroup>
              <FormGroup title={<SectionTitle icon={ImageIcon}>Gallery images</SectionTitle>} description="Optional. Uploaded as real files, not linked. The first one is the primary image.">
                <div className="col-span-full flex flex-col gap-4">
                  {canWrite ? (
                    <Upload
                      accept="img"
                      multiple
                      disabled={gallery.busy}
                      onChange={gallery.onChange}
                      aria-label="Product gallery image files"
                      placeholderText={gallery.busy ? <UploadBusyHint label="Uploading…" /> : <UploadDropHint />}
                      iconClassName="size-8 shrink-0"
                      className="p-4"
                    />
                  ) : null}
                  {images.length > 0 ? (
                    <div className="flex flex-wrap gap-3">
                      {images.map((hash) => (
                        <div key={hash} className="relative">
                          <img src={imageUrl(hash)} alt="Uploaded product image" className="h-20 w-20 rounded-lg border border-muted object-cover" />
                          {canWrite ? (
                            <ActionIcon
                              type="button"
                              size="sm"
                              variant="solid"
                              color="danger"
                              className="absolute -right-2 -top-2"
                              aria-label="Remove this image"
                              onClick={() => removeImage(hash)}
                            >
                              <RemoveIcon aria-hidden className="size-3.5" />
                            </ActionIcon>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
                {gallery.error ? <ErrorSurface error={gallery.error} action="upload these images" /> : null}
              </FormGroup>

              {canWrite ? (
                <FormGroup
                  title={<SectionTitle icon={ImageIcon}>Packing image</SectionTitle>}
                  description="The one image a warehouse packing device caches for this product — separate from the storefront images above."
                >
                  <div className="col-span-full flex flex-wrap items-center gap-4">
                    <Upload
                      accept="img"
                      multiple={false}
                      disabled={packingImage.busy}
                      onChange={packingImage.onChange}
                      aria-label="Packing image file"
                      placeholderText={packingImage.busy ? <UploadBusyHint label="Uploading…" /> : <UploadDropHint />}
                      iconClassName="size-8 shrink-0"
                      className="p-4"
                      wrapperClassName="w-full max-w-xs"
                    />
                    {imageHash ? (
                      <div className="flex flex-col items-start gap-1.5">
                        <img
                          src={imageUrl(imageHash)}
                          alt={`${product.name} — uploaded packing image`}
                          className="h-20 w-20 rounded-lg border border-muted object-cover"
                        />
                        {uploadedImage ? (
                          <Text className="text-xs text-gray-500">
                            {uploadedImage.sizeBytes.toLocaleString()} bytes · {uploadedImage.contentType}
                          </Text>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  {packingImage.error ? <ErrorSurface error={packingImage.error} action="upload this image" /> : null}
                </FormGroup>
              ) : null}

              <FormGroup title={<SectionTitle icon={StatusIcon}>Status</SectionTitle>} description="Whether new SKUs can be created under this product.">
                <StatusSwitch
                  className="@xl:col-span-2"
                  disabled={!canWrite}
                  activeText="Offered for new SKUs."
                  inactiveText="Not offered for new SKUs. Nothing is deleted — existing SKUs, prices and past orders keep referring to it."
                  {...register("isActive")}
                />
              </FormGroup>
            </div>

            {canWrite ? (
              <FormFooter
                className={formCardFooter}
                submitBtnText={isSubmitting ? "Saving…" : "Save changes"}
                isLoading={isSubmitting}
                altBtnText="Cancel"
                handleAltBtn={goBack}
              />
            ) : null}
          </>
        )}
      </Form>
    </div>
  );
}
